import React from 'react';
import {
  ArrowUpOnSquareIcon,
  ChevronDownIcon,
  PhotoIcon,
  QueueListIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { EmailBlockData } from '../ModernEmailBuilder';
import { CustomColorPicker } from './PropertiesPanel';

export interface EditorialPropertyContext {
  block: EmailBlockData;
  handleContentChange: (key: string, value: unknown) => void;
  handleStyleChange: (key: string, value: unknown) => void;
  imageSourceDropRef: React.RefObject<HTMLDivElement | null>;
  isImageSourceDropOpen: boolean;
  setIsImageSourceDropOpen: (open: boolean) => void;
  imageTargetFieldRef: React.MutableRefObject<string | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isUploadingImage: boolean;
  setIsStockImageModalOpen: (open: boolean) => void;
  setContentManagerTab: (tab: 'image' | 'template' | 'file' | 'graphic' | 'media') => void;
  setIsContentManagerOpen: (open: boolean) => void;
  ImageSourceFromCanvaRow: React.FC<{ onClose: () => void }>;
  showColorPicker: string | null;
  setShowColorPicker: (id: string | null) => void;
  colorPickerRef: React.RefObject<HTMLDivElement | null>;
  getEmailBackgroundColors: () => string[];
  brandColors: { primary: string; secondary: string[] };
  showAdvancedPicker: string | null;
  setShowAdvancedPicker: (type: string | null) => void;
  setAdvancedPickerPosition: (pos: { top: number; left: number } | null) => void;
}

export function ImageSourceField({
  ctx,
  field = 'image',
  label = 'Add image from...',
}: {
  ctx: EditorialPropertyContext;
  field?: string;
  label?: string;
}) {
  const {
    block,
    handleContentChange,
    imageSourceDropRef,
    isImageSourceDropOpen,
    setIsImageSourceDropOpen,
    imageTargetFieldRef,
    fileInputRef,
    isUploadingImage,
    setIsStockImageModalOpen,
    setContentManagerTab,
    setIsContentManagerOpen,
    ImageSourceFromCanvaRow,
  } = ctx;

  const imageValue = block.content[field];

  return (
    <div className="space-y-2 relative" ref={imageSourceDropRef}>
      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsImageSourceDropOpen(!isImageSourceDropOpen)}
          className="w-full flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
            <span>My computer</span>
          </div>
          <ChevronDownIcon
            className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isImageSourceDropOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isImageSourceDropOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 py-1 bg-white border border-slate-100 rounded-xl shadow-xl z-[100]">
            <button
              type="button"
              onClick={() => {
                imageTargetFieldRef.current = field;
                setIsImageSourceDropOpen(false);
                if (!isUploadingImage) fileInputRef.current?.click();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-400" />
              <span className="font-medium">My computer</span>
            </button>
            <button
              type="button"
              onClick={() => {
                imageTargetFieldRef.current = field;
                setIsImageSourceDropOpen(false);
                setIsStockImageModalOpen(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <PhotoIcon className="w-4 h-4 text-slate-400" />
              <span className="font-medium">Stock image</span>
            </button>
            <button
              type="button"
              onClick={() => {
                imageTargetFieldRef.current = field;
                setIsImageSourceDropOpen(false);
                setContentManagerTab('image');
                setIsContentManagerOpen(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <QueueListIcon className="w-4 h-4 text-slate-400" />
              <span className="font-medium">Library</span>
            </button>
            <ImageSourceFromCanvaRow onClose={() => setIsImageSourceDropOpen(false)} />
            {imageValue && (
              <>
                <div className="h-px bg-slate-100 my-1 mx-2" />
                <button
                  type="button"
                  onClick={() => {
                    handleContentChange(field, '');
                    setIsImageSourceDropOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-600 hover:bg-rose-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span className="font-semibold">Remove Image</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ColorField({
  ctx,
  id,
  label,
  value,
  onChange,
  align = 'left',
}: {
  ctx: EditorialPropertyContext;
  id: string;
  label: string;
  value: string;
  onChange: (color: string) => void;
  align?: 'left' | 'right';
}) {
  const {
    showColorPicker,
    setShowColorPicker,
    colorPickerRef,
    getEmailBackgroundColors,
    brandColors,
    showAdvancedPicker,
    setShowAdvancedPicker,
    setAdvancedPickerPosition,
  } = ctx;

  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold text-slate-700">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowColorPicker(showColorPicker === id ? null : id)}
          className="w-full h-10 px-2 rounded-xl border border-slate-200 flex items-center gap-3 bg-white hover:bg-slate-50 shadow-sm"
        >
          <div className="w-6 h-6 rounded-lg shadow-inner border border-black/5" style={{ backgroundColor: value }} />
          <span className="text-xs font-mono text-slate-400 uppercase">{value.replace('#', '')}</span>
        </button>
        {showColorPicker === id && (
          <div ref={colorPickerRef} className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-2 z-[101]`}>
            <CustomColorPicker
              currentColor={value}
              onChange={onChange}
              pickerType="solid"
              emailColors={getEmailBackgroundColors()}
              brandColors={brandColors}
              showAdvancedPicker={showAdvancedPicker}
              setShowAdvancedPicker={setShowAdvancedPicker}
              setAdvancedPickerPosition={setAdvancedPickerPosition}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ShowButtonToggle({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="flex items-center justify-between py-1 px-1">
      <label className="text-sm font-medium text-slate-700">Show Button</label>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={block.content.showButton !== false}
          onChange={(e) => handleContentChange('showButton', e.target.checked)}
        />
        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500" />
      </label>
    </div>
  );
}

function CtaFields({ ctx, defaultButtonPadding = '16px 40px' }: { ctx: EditorialPropertyContext; defaultButtonPadding?: string }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="space-y-4 pt-2 border-t border-slate-100">
      <div className="flex items-center justify-between mb-4 px-1">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Call to Action</label>
        <div className="h-0.5 flex-1 bg-slate-100 ml-4" />
      </div>
      <ShowButtonToggle ctx={ctx} />
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-700">Button Padding</label>
        <input
          type="text"
          value={block.content.buttonPadding || defaultButtonPadding}
          onChange={(e) => handleContentChange('buttonPadding', e.target.value)}
          placeholder="16px 40px"
          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Button Text</label>
          <input
            type="text"
            value={block.content.buttonText || ''}
            onChange={(e) => handleContentChange('buttonText', e.target.value)}
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Button URL</label>
          <input
            type="text"
            value={block.content.buttonUrl || ''}
            onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
            placeholder="#"
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
          />
        </div>
      </div>
    </div>
  );
}

export function HeroEditorialPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4 px-1">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Content</label>
          <div className="h-0.5 flex-1 bg-slate-100 ml-4" />
        </div>
        <ImageSourceField ctx={ctx} />
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Subtitle</label>
          <input
            type="text"
            value={block.content.subtitle || ''}
            onChange={(e) => handleContentChange('subtitle', e.target.value)}
            placeholder="SPRING / SUMMER 2024"
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Heading</label>
          <textarea
            value={block.content.heading || ''}
            onChange={(e) => handleContentChange('heading', e.target.value)}
            rows={2}
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Body Text</label>
          <textarea
            value={block.content.text || ''}
            onChange={(e) => handleContentChange('text', e.target.value)}
            rows={3}
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-white shadow-sm"
          />
        </div>
      </div>
      <CtaFields ctx={ctx} />
    </div>
  );
}

export function HeroQuotePropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4 px-1">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Quote</label>
          <div className="h-0.5 flex-1 bg-slate-100 ml-4" />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Quote Text</label>
          <textarea
            value={block.content.text || ''}
            onChange={(e) => handleContentChange('text', e.target.value)}
            rows={4}
            placeholder="Your quote here..."
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Attribution</label>
          <input
            type="text"
            value={block.content.subtitle || ''}
            onChange={(e) => handleContentChange('subtitle', e.target.value)}
            placeholder="AUTHOR NAME"
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-slate-500/10 bg-white shadow-sm"
          />
        </div>
      </div>
    </div>
  );
}

export function HeroCardExtraColorFields({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="grid grid-cols-2 gap-4 pt-2">
      <ColorField
        ctx={ctx}
        id="heroCardText"
        label="Heading Color"
        value={block.content.textColor || '#000000'}
        onChange={(c) => handleContentChange('textColor', c)}
      />
      <ColorField
        ctx={ctx}
        id="heroCardSubtitle"
        label="Subtitle Color"
        value={block.content.subtitleColor || '#6B7280'}
        onChange={(c) => handleContentChange('subtitleColor', c)}
        align="right"
      />
      <ColorField
        ctx={ctx}
        id="heroCardBody"
        label="Body Text Color"
        value={block.content.bodyTextColor || '#4B5563'}
        onChange={(c) => handleContentChange('bodyTextColor', c)}
      />
      <ColorField
        ctx={ctx}
        id="heroCardBtnText"
        label="Button Text Color"
        value={block.content.buttonTextColor || '#FFFFFF'}
        onChange={(c) => handleContentChange('buttonTextColor', c)}
        align="right"
      />
    </div>
  );
}

export function ImageContentEditorialExtras({ ctx }: { ctx: EditorialPropertyContext }) {
  return (
    <div className="space-y-4 pt-4 border-t border-slate-100">
      <CtaFields ctx={ctx} defaultButtonPadding="14px 32px" />
    </div>
  );
}

export function ProductEditorialPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange } = ctx;
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <ImageSourceField ctx={ctx} label="Add product image from..." />
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Subtitle</label>
          <input
            type="text"
            value={block.content.subtitle || ''}
            onChange={(e) => handleContentChange('subtitle', e.target.value)}
            placeholder="LIMITED EDITION"
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Product Name</label>
          <input
            type="text"
            value={block.content.name || ''}
            onChange={(e) => handleContentChange('name', e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Description</label>
          <textarea
            value={block.content.description || ''}
            onChange={(e) => handleContentChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Price</label>
          <input
            type="text"
            value={block.content.price || ''}
            onChange={(e) => handleContentChange('price', e.target.value)}
            placeholder="$220.00"
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
          />
        </div>
      </div>
      <div className="space-y-4 pt-2 border-t border-slate-100">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700">Button Text</label>
            <input
              type="text"
              value={block.content.buttonText || ''}
              onChange={(e) => handleContentChange('buttonText', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700">Product URL</label>
            <input
              type="text"
              value={block.content.url || ''}
              onChange={(e) => handleContentChange('url', e.target.value)}
              placeholder="#"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-700">Button Padding</label>
          <input
            type="text"
            value={block.content.buttonPadding || '16px 48px'}
            onChange={(e) => handleContentChange('buttonPadding', e.target.value)}
            placeholder="16px 48px"
            className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ColorField
            ctx={ctx}
            id="productEditorialBtnBg"
            label="Button Color"
            value={block.content.buttonColor || '#111827'}
            onChange={(c) => handleContentChange('buttonColor', c)}
          />
          <ColorField
            ctx={ctx}
            id="productEditorialBtnText"
            label="Button Text Color"
            value={block.content.buttonTextColor || '#FFFFFF'}
            onChange={(c) => handleContentChange('buttonTextColor', c)}
            align="right"
          />
        </div>
      </div>
    </div>
  );
}
